import React, { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import cx from 'classnames';
import { reverse } from 'lodash/array';
import { orderBy } from 'lodash/collection';
import { isFunction } from 'lodash/lang';
import moment from 'moment';

import LogTableApi from '../../../../../../api/patient/LogTableApi';
import Common from '../../../../../../common/common';
import ModalBoxes from '../../../../../../common/feedback/ModalBoxes/ModalBoxes';
import Icon from '../../../../../../common/general/Icon';
import { scBlack, scDarkBlue100, scGreen100 } from '../../../../../../constants/systemColors';

import { ParticipantsButton } from './ParticipantsButton/ParticipantsButton';
import LogRowSignaturesHistoryModal from './LogRowSignaturesHistoryModal';
import { getPreparedData } from './LogsHistoryService';

import './LogHistory.scss';

export default function LogsHistory({ rowId, onHistoryDataLoad, className }) {
  const [logsHistory, setLogsHistory] = useState({});
  const LINKED_LOG_TRIGGERS = ['LINK_LOG_TO', 'LINK_LOG_FROM'];
  const UNLINKED_LOG_TRIGGERS = ['UNLINK_LOG', 'SUBJECT_REMOVED'];
  const LINK_LOG_RELATED_TRIGGERS = [...LINKED_LOG_TRIGGERS, ...UNLINKED_LOG_TRIGGERS];

  useEffect(
    function() {
      LogTableApi.getLogsHistory(rowId).then(({ data }) => {
        setLogsHistory(data);
      });
    },
    [rowId]
  );

  const isLastStatusCanceled = useMemo(() => {
    return orderBy(logsHistory.statuses, 'snapshotVersion', 'desc')[0]?.smStatus === 'CANCELED';
  }, [logsHistory.statuses]);

  const rowVersions = useMemo(() => {
    const rowVersions = getPreparedData(
      logsHistory?.logHistoryData?.tableData,
      logsHistory?.signatures,
      logsHistory?.statuses,
      logsHistory?.logHistoryData?.metadata
    );
    const changedItems = [];
    Object.keys(rowVersions).forEach(rowVersion => {
      const element = rowVersions[rowVersion];
      const row = element.row;
      Object.values(row).forEach(item => {
        if (item.changed && !item.signed && !isLastStatusCanceled) {
          changedItems.push(item.question.id);
        }
      });
    });
    isFunction(onHistoryDataLoad) && onHistoryDataLoad(changedItems);
    return rowVersions;
  }, [logsHistory, isLastStatusCanceled, onHistoryDataLoad]);

  function openLogRowHistory() {
    ModalBoxes.open({
      component: (
        <LogRowSignaturesHistoryModal
          history={logsHistory?.logHistoryData}
          adverseEventHistory={logsHistory}
          rowVersions={rowVersions}
        />
      ),
      title: 'State of log at time of signature'
    });
  }

  const createdLogRow = logsHistory?.statuses?.find(row => row?.statusChangeTrigger === 'FIRST_ITEM_GROUP_SAVE');

  const linkedRow = useMemo(() => {
    return logsHistory?.statuses?.some(status => status.statusChangeTrigger?.includes('LINK_LOG'));
  }, [logsHistory?.statuses]);

  const firstRow = logsHistory?.logHistoryData?.tableData?.find(row => row?.rowVersion?.value === '1');

  function getLinkLogStatuses(statusList) {
    return statusList.filter(
      el =>
        LINKED_LOG_TRIGGERS.includes(el.statusChangeTrigger) || UNLINKED_LOG_TRIGGERS.includes(el.statusChangeTrigger)
    );
  }

  function getCompleteOrCanceledNotLinked(statusList) {
    return statusList?.filter(
      el =>
        (el.smStatus === 'COMPLETE' || el.smStatus === 'CANCELED') &&
        !LINK_LOG_RELATED_TRIGGERS.includes(el.statusChangeTrigger)
    );
  }

  const firstLinkDate = useMemo(() => {
    return logsHistory?.statuses?.find(status => status.statusChangeTrigger === 'LINK_LOG_TO')?.createdAt || null;
  }, [logsHistory?.statuses]);

  function showCancelledHistory(el) {
    return (
      (el.smStatus === 'CANCELED' && !LINK_LOG_RELATED_TRIGGERS.includes(el.statusChangeTrigger)) ||
      (el.smStatus === 'CANCELED' && el.statusChangeTrigger === 'UNLINK_LOG')
    );
  }

  return (
    <div className={cx('ae-log-history mt-2', className)}>
      {rowVersions &&
        reverse(Object.keys(rowVersions)).map(rowVersion => {
          const element = rowVersions[rowVersion];
          const row = element.row;
          const linkStatuses = getLinkLogStatuses(element?.status)?.map(s => ({ ...s, link: true }));

          const completeOrCanceled = getCompleteOrCanceledNotLinked(element?.status);

          let signatures = [];
          if (element?.signature) {
            signatures = [...element?.signature]?.map(s => ({ ...s, signature: true }));
          }

          const allStatusesPerSnapshotVersion = [...signatures, ...linkStatuses, ...completeOrCanceled].sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );

          return (
            <div className={'version-container'} key={rowVersion}>
              {allStatusesPerSnapshotVersion.map(el => (
                <>
                  {showCancelledHistory(el) && <CancelElement status={el} />}
                  {el.smStatus === 'COMPLETE' && el.statusChangeTrigger === 'SM_REVIEW_CONFIRMATION' && (
                    <Status status={el} onClick={openLogRowHistory} />
                  )}
                  {el.link &&
                    LINKED_LOG_TRIGGERS.includes(el.statusChangeTrigger) &&
                    (el.linkedPatientCount !== null || el.primeRowPatientId !== null) && <Link link={el} />}
                  {el.link && el.statusChangeTrigger === 'UNLINK_LOG' && el?.linkedGroupId !== null && (
                    <LinkOff link={el} />
                  )}
                  {el.link && el.statusChangeTrigger === 'SUBJECT_REMOVED' && el?.linkedGroupId !== null && (
                    <LinkOffForRemainingLogs link={el} />
                  )}
                  {el.signature &&
                    (el?.linkedGroupId !== null ? (
                      <SignatureForLinkedRows
                        signature={el}
                        onClick={openLogRowHistory}
                        firstLinkDate={firstLinkDate}
                      />
                    ) : (
                      <Signature signature={el} onClick={openLogRowHistory} />
                    ))}
                </>
              ))}

              {Object.values(row).map((item, id) => {
                return (
                  item.changed && (
                    <HistoryElement
                      key={`${rowVersion} ${id}`}
                      item={item}
                      historyStatuses={element?.status}
                      isLastStatusCanceled={isLastStatusCanceled}
                    />
                  )
                );
              })}
            </div>
          );
        })}

      {((linkedRow && createdLogRow) || (!linkedRow && firstRow)) && (
        <div className={'version-container'}>
          <div className={'question-answer-container'}>
            <div className={'answers'} style={{ justifyContent: 'start' }}>
              <div className={'section-title created-status-section'}>
                {linkedRow && createdLogRow && <LogCreatedForLinked row={createdLogRow} />}
                {!linkedRow && firstRow && <LogCreatedForNonLinked row={firstRow} />}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function HistoryElement({ item, historyStatuses, isLastStatusCanceled }) {
  const itemGroupUpdateStatus = historyStatuses.filter(el => el.statusChangeTrigger === 'ITEM_GROUP_UPDATE')[0];

  return (
    <div className={'question-answer-container'}>
      <span className={'question-name'}>{item.question?.header} ?</span>
      <span>
        Last Updated By {item.currentAnswer?.lastUpdatedBy?.value} on{' '}
        {moment(item.currentAnswer.lastUpdatedDate.value).format(dateFormatHistory)}{' '}
        {itemGroupUpdateStatus &&
          itemGroupUpdateStatus?.linkedGroupId !== null &&
          '(Updated through linked log record)'}
      </span>
      <div
        className={cx('answers', {
          highlighted: !item.signed && !isLastStatusCanceled
        })}
      >
        <Column item={item.previousAnswer} title={'Previous Data'} />
        <Column item={item.currentAnswer} title={'Current Data'} />
        <div className={'answer-section'}>
          <div className={'section-title'}>Reason For Change</div>
          <span className={'answer-name'}>{item.currentAnswer.reasonForDataChange || '--'}</span>
        </div>
      </div>
    </div>
  );
}

function LogCreatedForLinked({ row }) {
  return `Log Created by ${row.createdBy} at ${moment(row?.createdAt).format(dateFormatHistory)}`;
}

function LogCreatedForNonLinked({ row }) {
  return `Log Created by ${row['Last Updated By']?.value} at ${Common.formatDate(
    row['Last Updated Date']?.value,
    dateFormatHistory
  )}`;
}

function Column({ title, item }) {
  const { value, comment, performedStatus } = item;
  return (
    <div className={'answer-section'}>
      <div className={'section-title'}>{title}</div>
      <span className={'answer-name'}>
        {performedStatus ? `${performedStatus}: ${comment}` : ''}
        {value && value}
        {!value && !performedStatus && '--'}
      </span>
    </div>
  );
}

export function Status({ status, onClick }) {
  return (
    <div className={'status-container'}>
      <Icon style={{ color: scGreen100, fontSize: 24 }}>check_circle</Icon>
      <div
        style={{ color: scDarkBlue100, textDecoration: 'underline', marginLeft: '14px', cursor: 'pointer' }}
        onClick={onClick}
      >
        Marked Complete {moment(status?.createdAt).format(dateFormatHistory)}
      </div>
      <div>
        {status?.createdBy}
        {status?.userRole ? ' - ' + status.userRole : ''}
      </div>
    </div>
  );
}

export function CancelElement({ status }) {
  return (
    <div className={'cancel-reason-container'}>
      <span className={'question-name'}> Canceled By: </span>
      <span>
        {`${status.createdBy} on 
        ${moment(status.createdAt).format(dateFormatHistory)}`}
      </span>
      <div className={'cancel-reason'}>
        <div className={'reason-section'}>
          <div className={'cancel-reason-title'}>Reason For Cancellation</div>
          <span className={'reason-name'}>{status.reason || '--'}</span>
        </div>
      </div>
    </div>
  );
}

export function Signature({ signature, onClick }) {
  return (
    <div className="signature-container">
      <Icon style={{ color: scDarkBlue100, fontSize: 30 }}>rate_review</Icon>
      <SignatureText text="Signed Off" date={signature?.createdAt} onClick={onClick} />
      <SignatureInfo
        creatorUserName={signature?.creatorUserName}
        signerRole={signature?.signerRole}
        groupName={signature?.groupName}
      />
    </div>
  );
}

export function SignatureForLinkedRows({ signature, onClick, firstLinkDate }) {
  if (firstLinkDate && signature?.createdAt < firstLinkDate) {
    return <Signature signature={signature} onClick={onClick} />;
  }
  let text;
  if (signature?.signedPatientId !== null) {
    text = `Signed Off by Linked Log Record for ${signature?.signedPatientId}`;
  } else {
    text = 'Signed Off as Linked Log Record';
  }

  return (
    <div className="signature-container">
      <Icon style={{ color: scDarkBlue100, fontSize: 30 }}>rate_review</Icon>
      {text && (
        <>
          <SignatureText text={text} date={signature?.createdAt} onClick={onClick} />
          <SignatureInfo creatorUserName={signature?.creatorUserName} signerRole={signature?.signerRole} />
        </>
      )}
    </div>
  );
}

function SignatureInfo({ creatorUserName, signerRole, groupName }) {
  return (
    <div>
      {creatorUserName} - {signerRole}
      {groupName && `, ${groupName}`}
    </div>
  );
}

function SignatureText({ text, date, onClick }) {
  return (
    <div
      style={{
        color: scDarkBlue100,
        textDecoration: 'underline',
        marginLeft: '10px',
        cursor: 'pointer'
      }}
      onClick={onClick}
    >
      {text} {moment(date).format(dateFormatHistory)}
    </div>
  );
}

export function Link({ link }) {
  const formattedDate = moment(link?.createdAt).format(dateFormatHistory);
  const baseText = ` by ${link?.createdBy} at ${formattedDate}.`;

  const displayText =
    link?.statusChangeTrigger === 'LINK_LOG_TO' ? (
      `Log Linked to Patient ID ${link?.primeRowPatientId} ${baseText}`
    ) : (
      <>
        Log Linked to
        <ParticipantsButton statusHistoryId={link?.id} participantsCount={link?.linkedPatientCount} linked={true} />
        {baseText}
      </>
    );

  return (
    <div className="signature-container" data-testid="link-log-history-record">
      <Icon style={{ color: scDarkBlue100, fontSize: 36 }}>link</Icon>
      <Box sx={{ color: scBlack, fontWeight: 'bold', marginLeft: '10px', display: 'flex', alignItems: 'baseline' }}>
        {displayText}
      </Box>
    </div>
  );
}

export function LinkOff({ link }) {
  return (
    <div className={'signature-container'}>
      <Icon style={{ color: scDarkBlue100, fontSize: 36 }}>link_off</Icon>
      <div style={{ color: scBlack, fontWeight: 'bold', marginLeft: '10px' }}>
        Unlinked {moment(link?.createdAt).format(dateFormatHistory)} {link?.createdBy} - {link?.userRole}.
      </div>
    </div>
  );
}

export function LinkOffForRemainingLogs({ link }) {
  return (
    <div className={'signature-container'} data-testid="unlink-log-history-record">
      <Icon data-testid={'subjects-removed'} style={{ color: scDarkBlue100, fontSize: 36 }}>
        link_off
      </Icon>
      <Box sx={{ color: scBlack, fontWeight: 'bold', marginLeft: '10px', display: 'flex', alignItems: 'baseline' }}>
        <ParticipantsButton statusHistoryId={link?.id} participantsCount={link?.linkedPatientCount} linked={false} />
        unlinked on {moment(link?.createdAt).format(dateFormatHistory)} {' by '} {link?.createdBy} - {link?.userRole}.
      </Box>
    </div>
  );
}

export const dateFormatHistory = 'DD/MMM/YYYY h:mm:ss A';
